"use client";

import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { createClient } from "@/app/utils/supabase/clients";
import { Session, AuthChangeEvent } from "@supabase/supabase-js";
import posthog from "posthog-js";
import { clearSeriesCache } from "@/lib/cache/seriesCacheClient";
import { clearTrendSeriesCache } from "@/components/Trends/data/trendSeriesCache";
import { useTrendsStore } from "@/components/Trends/store/useTrendsStore";
import { useSavedTrendsStore } from "@/components/Trends/store/useSavedTrendsStore";

type AuthUserType = {
  auth_user_id: string;
};

type UserDetails = {
  id: string;
  email?: string;
  last_tos_date?: string;
  roles: string[];
  name?: string;
  first_name?: string | null;
  last_name?: string | null;
};

export type SubscriptionInfo = {
  plan: string | null;
  status: string | null;
  stripeSubscriptionId: string | null;
  stripeSubscriptionStatus: string | null;
  interval: string | null;
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
};

type AuthContextType = {
  user: AuthUserType | null;
  loading: boolean;
  userDetails: UserDetails;
  refreshUserDetails: () => Promise<void>;
  dataTiersVisible: number[];
  subscriptionInfo: SubscriptionInfo | null;
  refreshSubscriptionInfo: () => Promise<void>;
  // Bumped whenever the access token was re-minted because its role claims
  // had gone stale. Data fetched with the old token (e.g. gauge series) should
  // list this as an effect dependency and refetch when it changes.
  entitlementsVersion: number;
};

/**
 * The auth server turned the stored session down: its user is gone or its
 * token is no longer valid (401/403). A network failure has no status and
 * isn't a rejection, so an offline viewer keeps their session.
 */
export function isRejectedSession(error: unknown): boolean {
  const status = (error as { status?: unknown } | null)?.status;
  return status === 401 || status === 403;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  loading: true,
  userDetails: {
    id: "",
    roles: [],
  },
  refreshUserDetails: async () => {},
  dataTiersVisible: [0],
  subscriptionInfo: null,
  refreshSubscriptionInfo: async () => {},
  entitlementsVersion: 0,
});

// A failed roles fetch must not be a terminal state: roles gate the whole UI,
// and one dropped request (e.g. the post-sign-in cookie race) would otherwise
// leave the session role-less until the next login. Retry with exponential
// backoff: 1s, 2s, 4s, 8s, 16s, then give up until something else (auth
// event, realtime change, manual refresh) triggers a fetch again.
const USER_DETAILS_MAX_RETRIES = 5;
const USER_DETAILS_RETRY_BASE_MS = 1000;

// custom_access_token_hook bakes the user's roles into the JWT at mint time,
// and the series cache service authorizes gauge data from those claims — not
// from the database. When roles change server-side mid-session (invite-signup
// race, Retool grant, Stripe webhook sync), the token keeps its old roles
// until natural expiry or re-login, so gauges stay wrongly locked/unlocked.
// When we detect the DB roles diverging from the token's claims we force a
// token refresh, capped so a broken hook can't put us in a refresh loop.
const TOKEN_ROLES_MAX_REFRESHES = 3;

// Roles claim from a Supabase access token (base64url-encoded JWT payload),
// or null if the token can't be parsed.
function rolesFromAccessToken(accessToken: string): string[] | null {
  try {
    const payload = JSON.parse(
      atob(accessToken.split(".")[1].replace(/-/g, "+").replace(/_/g, "/")),
    );
    const roles = payload?.app_metadata?.roles;
    return Array.isArray(roles) ? roles : [];
  } catch {
    return null;
  }
}

function sameRoleSet(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  const sortedA = [...a].sort();
  const sortedB = [...b].sort();
  return sortedA.every((role, index) => role === sortedB[index]);
}

function getUserVisibleTiers(userDetails: UserDetails): number[] {
  const tiers = [0];
  if (userDetails.roles.includes("ds_plus")) {
    tiers.push(1);
  }
  if (userDetails.roles.includes("ds_pro")) {
    tiers.push(3);
  }
  if (userDetails.roles.includes("ds_preview")) {
    tiers.push(2);
  }
  return tiers;
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AuthUserType | null>(null);
  const [loading, setLoading] = useState(true);
  const [userDetails, setUserDetails] = useState<UserDetails>({
    id: "",
    roles: [],
  });
  const [subscriptionInfo, setSubscriptionInfo] =
    useState<SubscriptionInfo | null>(null);
  const [entitlementsVersion, setEntitlementsVersion] = useState(0);
  const dataTiersVisible = useMemo<number[]>(
    () => getUserVisibleTiers(userDetails),
    [userDetails],
  );
  const fetchingUserDetails = useRef(false);
  const fetchingSubscription = useRef(false);
  const retryTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const retryAttempt = useRef(0);
  const refreshingToken = useRef(false);
  const tokenRefreshCount = useRef(0);
  // The signed-in user this tab last saw: undefined until the first session
  // check, null when signed out.
  const lastUserId = useRef<string | null | undefined>(undefined);
  // Ref indirection so the retry timeout always calls the latest fetch
  // without making fetchUserDetails depend on itself.
  const fetchUserDetailsRef = useRef<() => Promise<void>>(async () => {});

  const supabase = createClient();

  const clearRetry = useCallback(() => {
    if (retryTimer.current) {
      clearTimeout(retryTimer.current);
      retryTimer.current = null;
    }
    retryAttempt.current = 0;
  }, []);

  const fetchUserDetails = useCallback(async () => {
    // Prevent simultaneous fetches
    if (fetchingUserDetails.current) {
      return;
    }

    fetchingUserDetails.current = true;
    let hadSession = false;
    let succeeded = false;
    try {
      // Fetch via server route so roles come back from the admin client
      // (bypassing RLS). We pull the access token from supabase-js and pass
      // it explicitly as a Bearer header — this avoids the cookie-write
      // race that happens right after sign-in where /api/user/me would 401
      // because the cookie hadn't propagated to fetch() yet.
      const { data: sessionData } = await supabase.auth.getSession();
      hadSession = Boolean(sessionData.session);
      const accessToken = sessionData.session?.access_token;
      const headers: Record<string, string> = {};
      if (accessToken) headers["Authorization"] = `Bearer ${accessToken}`;

      const res = await fetch("/api/user/me", {
        cache: "no-store",
        headers,
      });
      if (!res.ok) {
        console.error("Error fetching user details:", res.status);
        return;
      }
      succeeded = true;
      const data = (await res.json()) as {
        id: string;
        email?: string;
        name?: string;
        first_name?: string | null;
        last_name?: string | null;
        last_tos_date?: string;
        roles: string[];
      };

      const newRoles: string[] = data.roles ?? [];
      console.log(
        "👤 [AuthContext]: Fetched user details",
        "roles:",
        newRoles,
        "for user:",
        data.email || "unknown",
      );

      // Only update if data has actually changed
      setUserDetails((prev) => {
        // Check if roles have actually changed using deep comparison
        const prevRoles = prev.roles || [];
        const rolesChanged =
          prevRoles.length !== newRoles.length ||
          prevRoles.some((role, index) => role !== newRoles[index]);

        const otherDataChanged =
          prev.id !== data.id ||
          prev.email !== data.email ||
          prev.name !== data.name ||
          prev.first_name !== data.first_name ||
          prev.last_name !== data.last_name ||
          prev.last_tos_date !== data.last_tos_date;

        if (rolesChanged || otherDataChanged) {
          if (rolesChanged) {
            console.log(
              "👤 [AuthContext]: User roles updated -",
              newRoles.join(", ") || "none",
            );
          }
          return {
            ...data,
            // Preserve the existing roles array reference if roles haven't changed
            roles: rolesChanged ? newRoles : prev.roles,
          } as UserDetails;
        }

        return prev;
      });

      // Keep the access token's role claims in sync with the DB. If they
      // diverge, re-mint the token (refreshSession re-runs the custom claims
      // hook), drop the series response caches (gauges' and Trends') poisoned
      // by the old token, and bump entitlementsVersion so token-authorized data
      // (gauges) refetches. Trends reloads on its own once its cache is cleared.
      if (accessToken) {
        const tokenRoles = rolesFromAccessToken(accessToken);
        if (tokenRoles && sameRoleSet(tokenRoles, newRoles)) {
          tokenRefreshCount.current = 0;
        } else if (
          tokenRoles &&
          !refreshingToken.current &&
          tokenRefreshCount.current < TOKEN_ROLES_MAX_REFRESHES
        ) {
          refreshingToken.current = true;
          tokenRefreshCount.current += 1;
          console.log(
            "👤 [AuthContext]: Access token role claims are stale — refreshing token",
            "token:",
            tokenRoles,
            "db:",
            newRoles,
          );
          try {
            const { error: refreshError } =
              await supabase.auth.refreshSession();
            if (refreshError) {
              console.error("Error refreshing session token:", refreshError);
            } else {
              clearSeriesCache();
              clearTrendSeriesCache();
              setEntitlementsVersion((version) => version + 1);
            }
          } finally {
            refreshingToken.current = false;
          }
        }
      }
    } catch (error) {
      console.error("Error fetching user details:", error);
    } finally {
      fetchingUserDetails.current = false;
      setLoading(false);
      if (succeeded) {
        clearRetry();
      } else if (
        hadSession &&
        retryAttempt.current < USER_DETAILS_MAX_RETRIES &&
        !retryTimer.current
      ) {
        // A signed-in session with no roles is broken until this succeeds —
        // retry. Signed-out failures (no session) are expected and not retried.
        const delay = USER_DETAILS_RETRY_BASE_MS * 2 ** retryAttempt.current;
        retryAttempt.current += 1;
        console.warn(
          `👤 [AuthContext]: Roles fetch failed, retrying in ${delay}ms ` +
            `(attempt ${retryAttempt.current}/${USER_DETAILS_MAX_RETRIES})`,
        );
        retryTimer.current = setTimeout(() => {
          retryTimer.current = null;
          fetchUserDetailsRef.current();
        }, delay);
      }
    }
  }, [supabase.auth, clearRetry]);

  useEffect(() => {
    fetchUserDetailsRef.current = fetchUserDetails;
  }, [fetchUserDetails]);

  const refreshUserDetails = useCallback(async () => {
    if (user?.auth_user_id) {
      await fetchUserDetails();
    }
  }, [user?.auth_user_id, fetchUserDetails]);

  const fetchSubscriptionInfo = useCallback(async () => {
    if (fetchingSubscription.current) return;
    fetchingSubscription.current = true;
    try {
      const res = await fetch("/api/stripe/subscription");
      if (!res.ok) {
        setSubscriptionInfo(null);
        return;
      }
      const data = await res.json();
      setSubscriptionInfo({
        plan: data.plan ?? null,
        status: data.status ?? null,
        stripeSubscriptionId: data.stripeSubscriptionId ?? null,
        stripeSubscriptionStatus: data.stripeSubscriptionStatus ?? null,
        interval: data.interval ?? null,
        currentPeriodEnd: data.currentPeriodEnd ?? null,
        cancelAtPeriodEnd: Boolean(data.cancelAtPeriodEnd),
      });
    } catch (error) {
      console.error("Error fetching subscription info:", error);
      setSubscriptionInfo(null);
    } finally {
      fetchingSubscription.current = false;
    }
  }, []);

  const refreshSubscriptionInfo = useCallback(async () => {
    await fetchSubscriptionInfo();
  }, [fetchSubscriptionInfo]);

  useEffect(() => {
    if (user?.auth_user_id) {
      fetchSubscriptionInfo();
    } else {
      setSubscriptionInfo(null);
    }
  }, [user?.auth_user_id, fetchSubscriptionInfo]);

  useEffect(() => {
    // Note: no `authInitialized.current` guard — that was a Strict-Mode bug.
    // The guard's ref persists across React 18's intentional dev double-mount,
    // so after cleanup unsubscribes the listener the second mount short-
    // circuits and never re-subscribes. Result in dev: SIGNED_IN events
    // never fire, login appears to never load user data. The cleanup +
    // re-subscription handles double-mount correctly on its own.
    console.log("🔐 [AuthContext]: Initializing auth...");

    // Trends keeps what it loaded on the old access (a shared link's, when
    // signed out), so a different user, or none, starts it over. A signed-in
    // user's open trend and list go too: signing out in another tab, or the
    // session running out, doesn't reload this one, and they'd stay on screen.
    const noteUser = (userId: string | null) => {
      const previous = lastUserId.current;
      if (previous !== undefined && previous !== userId) clearTrendSeriesCache();
      if (previous && previous !== userId) {
        useTrendsStore.getState().reset();
        useSavedTrendsStore.getState().reset();
      }
      lastUserId.current = userId;
    };

    // Get initial session. The stored session is only what this browser
    // kept, so check it with the auth server first: one it rejects (its user
    // was deleted, e.g. by a local db reset, or its token was revoked) is
    // dropped here. Trusting it would show the viewer as signed in while the
    // middleware, which does check, sends every page back to /login, and
    // /login would send them straight on again, forever.
    supabase.auth
      .getSession()
      .then(async ({ data: { session } }: { data: { session: Session | null } }) => {
        if (!session) return null;
        const { error } = await supabase.auth.getUser();
        if (!isRejectedSession(error)) return session;
        console.warn("🔐 [AuthContext]: Stored session was rejected; signing out locally", error);
        await supabase.auth.signOut({ scope: "local" });
        return null;
      })
      .then((session: Session | null) => {
        noteUser(session?.user?.id ?? null);
        setUser(session?.user ? { auth_user_id: session.user.id } : null);
        if (session?.user) {
          console.log(
            "🔐 [AuthContext]: User session restored for",
            session.user.email,
          );
          fetchUserDetails();
          // Identify user in PostHog
          posthog.identify(session.user.id, {
            email: session.user.email,
          });
        } else {
          console.log("🔐 [AuthContext]: No active session found");
          setLoading(false); // Important: Set loading to false even when no user
          posthog.reset(); // Reset PostHog identification when no user
        }
      });

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(
      (event: AuthChangeEvent, session: Session | null) => {
        // The initial session is the getSession() check's above, once it's
        // known to be good.
        if (event === "INITIAL_SESSION") return;
        noteUser(session?.user?.id ?? null);
        setUser(session?.user ? { auth_user_id: session.user.id } : null);
        if (session?.user) {
          console.log("🔐 [AuthContext]: User signed in -", session.user.email);
          fetchUserDetails();
          posthog.identify(session.user.id, {
            email: session.user.email,
            last_sign_in: new Date().toISOString(),
          });
        } else {
          console.log("🔐 [AuthContext]: User signed out");
          clearRetry();
          setUserDetails({
            id: "",
            roles: [],
          });
          posthog.reset(); // Reset PostHog identification when user signs out
        }
      },
    );

    return () => {
      subscription.unsubscribe();
      clearRetry();
    };
  }, [supabase.auth, fetchUserDetails, clearRetry]);

  // Push-based entitlement invalidation. Roles are written server-side while
  // sessions are open — Stripe webhook syncs, Retool grants, migration
  // backfills — and this session's copy is otherwise a login-time snapshot.
  // Subscribe to this user's own user_roles rows (RLS scopes the events to
  // the subscriber) and refetch roles + subscription info on any change.
  // Requires user_roles in the supabase_realtime publication
  // (migration 20260715000300).
  //
  // The SUBSCRIBED callback also refetches: on first subscribe it closes the
  // gap between the initial fetch and the channel going live, and after a
  // dropped websocket reconnects it picks up anything missed while offline.
  useEffect(() => {
    if (!userDetails.id) return;

    const channel = supabase
      .channel(`user-roles-${userDetails.id}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "user_roles",
          filter: `user_id=eq.${userDetails.id}`,
        },
        () => {
          console.log(
            "👤 [AuthContext]: user_roles changed server-side — refetching",
          );
          fetchUserDetails();
          fetchSubscriptionInfo();
        },
      )
      .subscribe((status) => {
        if (status === "SUBSCRIBED") {
          fetchUserDetails();
        }
      });

    return () => {
      supabase.removeChannel(channel);
    };
  }, [userDetails.id, supabase, fetchUserDetails, fetchSubscriptionInfo]);

  // Memoize context value to prevent unnecessary re-renders
  const contextValue = useMemo(
    () => ({
      user,
      loading,
      userDetails,
      refreshUserDetails,
      dataTiersVisible,
      subscriptionInfo,
      refreshSubscriptionInfo,
      entitlementsVersion,
    }),
    [
      user,
      loading,
      userDetails,
      refreshUserDetails,
      dataTiersVisible,
      subscriptionInfo,
      refreshSubscriptionInfo,
      entitlementsVersion,
    ],
  );

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
